import PropTypes from 'prop-types';
import { Link, useActionData, useParams, useLocation, useFetcher, useNavigate } from 'react-router';
import Joi from 'joi';
import { addMember } from '@/api/projects';
import { USER_ROLES } from '@/consts';
import { parseErrors } from '@/utils/validators';
import { useState, useCallback, useEffect } from 'react';
import { searchMembers } from '@/api/projects';
import Autocomplete from '../core/Autocomplete';
import { settingsActions } from '@/stores/settingsStore';

const schema = Joi.object({
  email: Joi.string().email({ tlds: false }).required(),
  role: Joi.string()
    .allow(...USER_ROLES)
    .required(),
});

const AddMemberModal = ({ title }) => {
  const [query, setQuery] = useState('');
  const errors = useActionData();
  const { state } = useLocation();
  const { projectId } = useParams();
  const member = state?.member ?? {};
  const [email, setEmail] = useState(member?.email ?? '');
  const fetcher = useFetcher();
  const navigate = useNavigate();

  const fetchMembers = useCallback(
    async query => {
      const { data } = await searchMembers(projectId, query);
      return data;
    },
    [projectId],
  );

  const handleAddMember = data => {
    setQuery('');
    setEmail(data.email);
  };

  const handleChangeEmail = e => {
    setEmail(e.target.value);
    setQuery(e.target.value);
  };

  useEffect(() => {
    if (fetcher.state === 'idle' && fetcher.data && !fetcher.data.error) {
      settingsActions.skipGettingStartedStep(projectId, 'member');
      navigate(`/projects/${projectId}`);
    }
  }, [fetcher.state, fetcher.data, projectId, navigate]);

  return (
    <dialog className="modal" open>
      <div className="modal-box overflow-y-visible">
        <fetcher.Form method="post">
          <h3 className="font-bold text-lg">{title}</h3>
          <label className="form-control w-full">
            <div className="label">
              <span className="label-text">Email</span>
            </div>
            <input
              autoComplete="off"
              type="text"
              name="email"
              value={email}
              placeholder="Enter email"
              className="input input-bordered input-sm w-full"
              onChange={handleChangeEmail}
            />
            <Autocomplete query={query} onSelect={handleAddMember} onSearch={fetchMembers} />
            <div className="label">
              <span className="label-text-alt text-error">{errors?.email}</span>
            </div>
          </label>

          <label className="form-control w-full">
            <div className="label">
              <span className="label-text">Role</span>
            </div>
            <select
              name="role"
              defaultValue={member?.role}
              className="select select-bordered select-sm w-full capitalize"
            >
              <option value="">Choose role</option>
              {USER_ROLES.map(role => (
                <option key={role} value={role}>
                  {role}
                </option>
              ))}
            </select>
            <div className="label">
              <span className="label-text-alt text-error">{errors?.role}</span>
            </div>
          </label>

          <div className="modal-action">
            <Link className="btn btn-sm btn-secondary" to={`/projects/${projectId}`} replace>
              Cancel
            </Link>
            <button type="submit" className="btn btn-accent btn-sm">
              Save
            </button>
          </div>
        </fetcher.Form>
      </div>
    </dialog>
  );
};

AddMemberModal.propTypes = {
  title: PropTypes.string,
};

AddMemberModal.action = async ({ request, params }) => {
  const form = await request.formData();
  const member = Object.fromEntries(form);
  const { projectId } = params;
  const { error } = schema.validate(member, { abortEarly: false });

  if (error) {
    const errors = parseErrors(error);
    return errors;
  }

  try {
    await addMember(projectId, member);
    return { success: true };
  } catch (err) {
    console.error('adding member failed');
  }

  return {};
};

export default AddMemberModal;
